import {
  Alert,
  Button,
  Dialog,
  PinInput,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { QRCodeSVG } from "qrcode.react";
import { useTRPC } from "../trpc/client";

const TotpSetupContext = createContext<() => void>(() => {});

export function TotpSetupProvider({
  open,
  children,
}: {
  open: () => void;
  children: ReactNode;
}) {
  return (
    <TotpSetupContext.Provider value={open}>
      {children}
    </TotpSetupContext.Provider>
  );
}

export function useOpenTotpSetup() {
  return useContext(TotpSetupContext);
}

const EMPTY_VALUE = ["", "", "", "", "", ""];

export default function TotpSetupModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [value, setValue] = useState<string[]>(EMPTY_VALUE);
  const [error, setError] = useState<string | null>(null);
  const otp = value.join("");

  const setup = useQuery({
    ...trpc.users.totpSetup.queryOptions(),
    enabled: isOpen,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const confirm = useMutation(
    trpc.users.confirmTotp.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.users.get.queryKey(),
        });
        onClose();
      },
      onError: (err) => {
        setValue(EMPTY_VALUE);
        setError(err.message);
      },
    })
  );

  useEffect(() => {
    if (!isOpen) {
      setValue(EMPTY_VALUE);
      setError(null);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (confirm.isPending) return;
    onClose();
  };

  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      open={isOpen}
      onOpenChange={(e) => !e.open && handleClose()}
      size="sm"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header borderBottom="1px solid #ebecef80" p={4}>
              <Dialog.Title fontSize="lg" fontWeight="semibold">
                Configura tu Clave Dinámica
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger position="top-end" top={3} />
            <Dialog.Body py={6}>
              <VStack gap={4} align="stretch">
                <Text color="gray.600" fontSize="sm">
                  Escanea este código con tu app de autenticación. Luego ingresa
                  el código de 6 dígitos para confirmar.
                </Text>
                {setup.data?.uri && !setup.isFetching ? (
                  <VStack>
                    <QRCodeSVG value={setup.data.uri} size={180} />
                  </VStack>
                ) : (
                  <Text color="gray.500" fontSize="sm" textAlign="center">
                    {setup.isError
                      ? "No se pudo cargar el código"
                      : "Cargando código..."}
                  </Text>
                )}
                <VStack gap={2}>
                  <PinInput.Root
                    size="lg"
                    otp
                    value={value}
                    onValueChange={(details) => {
                      setError(null);
                      setValue(details.value);
                    }}
                  >
                    <PinInput.HiddenInput />
                    <PinInput.Control>
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <PinInput.Input key={index} index={index} />
                      ))}
                    </PinInput.Control>
                  </PinInput.Root>
                </VStack>
                {error && (
                  <Alert.Root status="error" colorPalette="red">
                    <Alert.Indicator>
                      <AlertCircle size={16} />
                    </Alert.Indicator>
                    <Alert.Content>
                      <Alert.Description fontSize="sm">
                        {error}
                      </Alert.Description>
                    </Alert.Content>
                  </Alert.Root>
                )}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer borderTop="1px solid #ebecef80" p={4}>
              <Button
                variant="ghost"
                onClick={handleClose}
                disabled={confirm.isPending}
              >
                Cerrar
              </Button>
              <Button
                colorPalette="palomma"
                loading={confirm.isPending}
                disabled={
                  otp.length !== 6 || !setup.data?.uri || setup.isFetching
                }
                onClick={() => confirm.mutate({ otp })}
              >
                Verificar
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
